import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-scroll';
import { FaGithub, FaLinkedin, FaTwitter, FaMobile, FaLaptop, FaBrain, FaEnvelopeSquare, FaEnvelope, FaInstagram } from 'react-icons/fa';
import { FaXTwitter } from "react-icons/fa6";
import { useLanguage } from '../context/LanguageContext';
import { useInView } from 'react-intersection-observer';
import { useTypewriter } from '../hooks/useTypewriter';

const FloatingIcon: React.FC<{ icon: React.ReactNode; delay: number; position: { x: number; y: number } }> = ({ 
  icon, 
  delay, 
  position 
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0 }}
      animate={{ 
        opacity: [0.3, 0.6, 0.3],
        scale: [1, 1.2, 1],
        x: [position.x - 20, position.x + 20, position.x - 20],
        y: [position.y - 20, position.y + 20, position.y - 20],
      }}
      transition={{
        duration: 8,
        delay,
        repeat: Infinity,
        ease: "easeInOut"
      }}
      className="absolute text-4xl text-primary/30 dark:text-primary-light/30"
      style={{ left: `${position.x}%`, top: `${position.y}%` }}
    >
      {icon}
    </motion.div>
  );
};

const Hero: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { t } = useLanguage();

  const titles = [
    t('hero.subtitle.fullstack'),
    t('hero.subtitle.webdev'),
    t('hero.subtitle.ai'),
  ];

  const animatedText = useTypewriter({
    words: titles,
    typingSpeed: 120,
    deletingSpeed: 80,
    delayBetweenWords: 3000,
  });  

  return (
    <section className="min-h-screen flex items-center justify-center relative overflow-hidden section-padding">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-0 w-48 sm:w-72 md:w-96 h-48 sm:h-72 md:h-96 bg-primary/10 dark:bg-primary-light/10 rounded-full filter blur-3xl animate-float" />
        <div className="absolute bottom-0 right-0 w-48 sm:w-72 md:w-96 h-48 sm:h-72 md:h-96 bg-secondary/10 dark:bg-secondary-light/10 rounded-full filter blur-3xl animate-float" style={{ animationDelay: '-3s' }} />
        
        {/* Floating Tech Icons */}
        <FloatingIcon icon={<FaMobile />} delay={0} position={{ x: 20, y: 30 }} />
        <FloatingIcon icon={<FaLaptop />} delay={2} position={{ x: 80, y: 40 }} />
        <FloatingIcon icon={<FaBrain />} delay={4} position={{ x: 50, y: 70 }} />
      </div>

      <div className="container mx-auto relative z-10 px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-center gap-4 sm:gap-6 md:gap-8">
          {/* Profile Picture */}
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-64 lg:h-64 rounded-full overflow-hidden border-4 border-primary dark:border-primary-light shadow-lg"
          >
            <img
              src="/images/profile_image.jpg"
              alt="Yasser Kouddan"
              className="w-full h-full object-cover rounded-full"
            />
          </motion.div>

          {/* Content */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-center md:text-left px-4 sm:px-6 md:px-8"
          >
            <h1 className="heading-primary mb-2 sm:mb-4">
              <span className="text-primary dark:text-primary-light">{t('hero.title')}</span>
            </h1>
            <h2 className="text-2xl md:text-3xl text-secondary mb-6 min-h-[2em]">
              {animatedText}
              <span className="animate-blink">|</span>
            </h2>
            <p className="text-sm sm:text-base max-w-2xl text-base-content/60 mb-6 sm:mb-8">
              {t('hero.description')}
            </p>

            {/* Social Links */}
            <div className="flex justify-center md:justify-start space-x-6 mb-8">
              <motion.a
                href="kouddan.yasser.work@gmail.com"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="text-base-content/60 hover:text-primary dark:hover:text-primary-light transition-colors"
              >
                <FaEnvelope size={24} />
              </motion.a>
              <motion.a
                href="https://github.com/Yasser-Kouddan"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="text-base-content/60 hover:text-primary dark:hover:text-primary-light transition-colors"
              >
                <FaGithub size={24} />
              </motion.a>
              <motion.a
                href="https://www.linkedin.com/in/yasser-kouddan-b6b000226"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="text-base-content/60 hover:text-primary dark:hover:text-primary-light transition-colors"
              >
                <FaLinkedin size={24} />
              </motion.a>
              <motion.a
                href="https://x.com/k_yasser_dev"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="text-base-content/60 hover:text-primary dark:hover:text-primary-light transition-colors"
              >
                <FaXTwitter size={24} />
              </motion.a>
              <motion.a
                href="https://www.instagram.com/yasser.kouddan.dev/"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="text-base-content/60 hover:text-primary dark:hover:text-primary-light transition-colors"
              >
                <FaInstagram size={24} />
              </motion.a>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row justify-center md:justify-start gap-4">
              <Link
                to="experience"
                smooth={true}
                duration={500}
                className="btn-primary"
              >
                {t('hero.viewExperience')}
              </Link>
              <Link
                to="projects"
                smooth={true}
                duration={500}
                className="btn-secondary"
              >
                {t('hero.seeProjects')}
              </Link>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        animate={{
          y: [0, 10, 0],
        }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          repeatType: "reverse",
        }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-base-content/30 rounded-full flex justify-center">
          <div className="w-1 h-3 bg-base-content/30 rounded-full mt-2" />
        </div>
      </motion.div>
    </section>
  );
};

export default Hero; 